// Routes (TanStack Router, code-based). Sign-in screens are public; everything under
// the `app` layout needs a session (and TOTP enrolment done when the role needs it).
import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { z } from 'zod';
import type * as OfficeScreensModule from '../office/screens/index.js';
import { AppearancePanel, PublicAppearanceScreen } from '../screens/AppearanceScreen.js';
import { NotFoundScreen, PinScreen } from '../screens/PinScreen.js';
import { RegisterTerminalScreen } from '../screens/RegisterTerminalScreen.js';
import { Shell } from '../screens/Shell.js';
import { SignInScreen } from '../screens/SignInScreen.js';
import { TotpEnrolScreen } from '../screens/TotpEnrolScreen.js';
import { tokens } from './api.js';
import { queryClient } from './query.js';
import { homeFor, outletPermissions } from '../lib/permissions.js';
import { readMe } from '../offline/shell-reads.js';
import { isTillRole, terminalStore } from './terminal.js';
import { unwrap } from '@qrkey/api-client';
import { api } from './api.js';

const rootRoute = createRootRoute({ component: Outlet, notFoundComponent: NotFoundScreen });

const loginSearch = z.object({ redirect: z.string().optional() });

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: loginSearch,
  component: function Login() {
    const { redirect: to } = loginRoute.useSearch();
    return <SignInScreen method="password" {...(to ? { redirect: to } : {})} />;
  },
});

const loginCodeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login/code',
  validateSearch: loginSearch,
  component: function LoginCode() {
    const { redirect: to } = loginCodeRoute.useSearch();
    return <SignInScreen method="otp" {...(to ? { redirect: to } : {})} />;
  },
});

const pinRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pin',
  component: PinScreen,
});

const appearanceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/appearance',
  component: PublicAppearanceScreen,
});

const totpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/setup/totp',
  beforeLoad: () => {
    if (!tokens.get()) throw redirect({ to: '/login' });
  },
  component: TotpEnrolScreen,
});

/** Signed-in area. */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: ({ location }) => {
    const session = tokens.get();
    if (!session) {
      const terminal = terminalStore.get();
      if (terminal && isTillRole(terminal.role)) throw redirect({ to: '/pin' });
      throw redirect({
        to: '/login',
        search: location.href === '/' ? {} : { redirect: location.href },
      });
    }
    if (session.session.totpEnrolmentRequired) throw redirect({ to: '/setup/totp' });
  },
  component: Shell,
});

const homeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  beforeLoad: async () => {
    const session = tokens.get();
    const me = await queryClient
      .fetchQuery({
        queryKey: ['me', session?.session.id],
        // Remembered for the offline till; built from the PIN bundle for a local session.
        queryFn: () => (session ? readMe(session) : unwrap(api.client.GET('/v1/me'))),
      })
      .catch(() => undefined);
    const terminal = terminalStore.get();
    const bound = terminal && session?.session.terminalId === terminal.terminalId ? terminal : null;
    throw redirect({
      to: homeFor(bound?.role, outletPermissions(me, session?.session.outletId ?? null)),
      replace: true,
    });
  },
});

// POS, captain and KDS (M3 task 7): loaded on first visit, so the back office never
// downloads them (and tills never download the back office).
const orderSearch = z.object({ order: z.uuid().optional() });

const posRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/pos',
  validateSearch: orderSearch,
  component: lazyRouteComponent(() => import('../pos/PosScreen.js'), 'PosPage'),
});

const captainRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/captain',
  validateSearch: orderSearch,
  component: lazyRouteComponent(() => import('../pos/PosScreen.js'), 'CaptainPage'),
});

const tillRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/till',
  component: lazyRouteComponent(() => import('../till/TillScreen.js'), 'TillPage'),
});

const kdsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/kds',
  validateSearch: z.object({ station: z.string().max(40).optional() }),
  component: lazyRouteComponent(() => import('../pos/KdsScreen.js'), 'KdsPage'),
});

/** Back office (M2 task 6): a layout with its section list, and one route per screen. */
// Loaded on first visit: tills never download the back office.
const officeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/office',
  component: lazyRouteComponent(() => import('../office/screens/index.js'), 'OfficeLayout'),
});

type OfficeScreens = typeof OfficeScreensModule;
const office = <P extends string>(path: P, name: keyof OfficeScreens) =>
  createRoute({
    getParentRoute: () => officeRoute,
    path,
    component: lazyRouteComponent(() => import('../office/screens/index.js'), name),
  });

const officeRoutes = [
  office('/', 'OverviewScreen'),
  office('/outlets', 'OutletsScreen'),
  office('/outlet', 'OutletProfileScreen'),
  office('/floor', 'FloorScreen'),
  office('/timings', 'TimingsScreen'),
  office('/charges', 'ChargesScreen'),
  office('/payment-methods', 'PaymentMethodsScreen'),
  office('/order-sources', 'OrderSourcesScreen'),
  office('/features', 'FeaturesScreen'),
  office('/terminals', 'TerminalsScreen'),
  office('/stations', 'StationsScreen'),
  office('/printers', 'PrintersScreen'),
  office('/menu', 'MenuScreen'),
  office('/menu/prices', 'PricesScreen'),
  office('/menu/publish', 'PublishScreen'),
  office('/menu/import', 'ImportScreen'),
  office('/menu/products/$productId', 'ProductRoute'),
  office('/taxes', 'TaxesScreen'),
  office('/discounts', 'DiscountsScreen'),
  office('/discount-limits', 'DiscountLimitsScreen'),
  office('/bill-preview', 'BillPreviewScreen'),
  office('/day-close', 'DayCloseScreen'),
  office('/cash', 'CashScreen'),
  office('/expenses', 'ExpensesScreen'),
  office('/stock', 'StockScreen'),
  office('/stock-items', 'StockItemsScreen'),
  office('/recipes', 'RecipesScreen'),
  office('/stock-in', 'StockInScreen'),
  office('/wastage', 'WastageScreen'),
  office('/stock-counts', 'StockCountsScreen'),
  office('/vendors', 'VendorsScreen'),
  office('/stock-ledger', 'StockLedgerScreen'),
  office('/stock-consumption', 'StockConsumptionScreen'),
  office('/liquor-profiles', 'LiquorProfilesScreen'),
  office('/permits', 'PermitsScreen'),
  office('/breakage', 'BreakageScreen'),
  office('/bar-closing', 'BarClosingScreen'),
  office('/reports/excise', 'ExciseReportScreen'),
  office('/reports/bar-consumption', 'BarConsumptionScreen'),
  office('/reports/sales', 'SalesReportScreen'),
  office('/reports/items', 'ItemsReportScreen'),
  office('/reports/tax', 'TaxReportScreen'),
  office('/reports/payments', 'PaymentsReportScreen'),
  office('/reports/voids-discounts', 'VoidsDiscountsReportScreen'),
  office('/customers', 'CustomersScreen'),
  office('/customers/import', 'CustomerImportScreen'),
  office('/customers/$customerId', 'CustomerDetailScreen'),
  office('/khata', 'KhataScreen'),
  office('/rewards', 'RewardsScreen'),
  office('/messages/templates', 'MessageTemplatesScreen'),
  office('/messages/settings', 'MessageSettingsScreen'),
  office('/messages/log', 'MessageLogScreen'),
  office('/messages/opt-outs', 'OptOutsScreen'),
  office('/aggregators', 'AggregatorsScreen'),
  office('/aggregators/items', 'AggregatorItemsScreen'),
  office('/aggregators/pushes', 'AggregatorPushesScreen'),
  office('/aggregators/availability', 'AggregatorAvailabilityScreen'),
  office('/aggregators/settlements', 'AggregatorSettlementsScreen'),
  office('/delivery/zones', 'DeliveryZonesScreen'),
  office('/delivery/slots', 'DeliverySlotsScreen'),
  office('/delivery/partners', 'DeliveryPartnersScreen'),
  office('/staff', 'StaffScreen'),
  office('/roles', 'RolesScreen'),
  office('/companies', 'CompaniesScreen'),
] as const;

const settingsAppearanceRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/appearance',
  component: AppearancePanel,
});

const registerTerminalRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/setup/terminal',
  component: RegisterTerminalScreen,
});

export const routeTree = rootRoute.addChildren([
  loginRoute,
  loginCodeRoute,
  pinRoute,
  appearanceRoute,
  totpRoute,
  appRoute.addChildren([
    homeRoute,
    posRoute,
    captainRoute,
    kdsRoute,
    tillRoute,
    officeRoute.addChildren([...officeRoutes]),
    settingsAppearanceRoute,
    registerTerminalRoute,
  ]),
]);

export const router = createRouter({ routeTree, defaultPreload: 'intent' });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
